/*
 * Reusable table-state hook: search filtering + pagination + sort.
 * Wrap any row[] with `useTableState(rows, { searchFields })` and the
 * hook returns:
 *   - search   / setSearch   (controlled string)
 *   - page     / setPage     (1-indexed)
 *   - pageSize / setPageSize
 *   - sortKey  / sortDir / toggleSort(key)
 *   - filtered                (rows after search)
 *   - sorted                  (filtered + sorted)
 *   - paged                   (sorted, sliced to current page)
 *   - totalRows / totalPages
 *
 * Search is case-insensitive, matches across `searchFields` (or every
 * value when omitted). Sort is alphabetical/numeric on the chosen key;
 * undefined sortKey leaves rows in input order. Page resets to 1
 * whenever search or sortKey changes so users never end up on an empty
 * page mid-filter.
 */

import { useEffect, useMemo, useState } from "react";

export type SortDir = "asc" | "desc";

export interface UseTableStateOptions<T> {
  initialPageSize?: number;
  searchFields?: Array<keyof T | string>;
  initialSortKey?: keyof T | string;
  initialSortDir?: SortDir;
}

export interface UseTableStateResult<T> {
  search: string;
  setSearch: (s: string) => void;
  page: number;
  setPage: (p: number) => void;
  pageSize: number;
  setPageSize: (n: number) => void;
  sortKey: string | null;
  sortDir: SortDir;
  toggleSort: (key: string) => void;
  filtered: T[];
  sorted: T[];
  paged: T[];
  totalRows: number;
  totalPages: number;
}

function valueAt<T extends Record<string, unknown>>(row: T, key: string): unknown {
  return row[key];
}

function compareValues(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true });
}

export function useTableState<T extends Record<string, unknown>>(
  rows: T[],
  opts: UseTableStateOptions<T> = {}
): UseTableStateResult<T> {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(opts.initialPageSize ?? 25);
  const [sortKey, setSortKey] = useState<string | null>((opts.initialSortKey as string) ?? null);
  const [sortDir, setSortDir] = useState<SortDir>(opts.initialSortDir ?? "asc");

  // Whenever the underlying input changes meaningfully (search/sort), the
  // current page may now be empty — reset to 1.
  useEffect(() => { setPage(1); }, [search, sortKey, sortDir, rows.length]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    const fields = (opts.searchFields ?? []) as string[];
    return rows.filter((r) => {
      if (fields.length) {
        return fields.some((f) => String(valueAt(r, f) ?? "").toLowerCase().includes(q));
      }
      // No fields specified: search every primitive value.
      for (const v of Object.values(r)) {
        if (v == null) continue;
        if (String(v).toLowerCase().includes(q)) return true;
      }
      return false;
    });
  }, [rows, search, opts.searchFields]);

  const sorted = useMemo(() => {
    if (!sortKey) return filtered;
    const arr = [...filtered];
    arr.sort((a, b) => {
      const cmp = compareValues(valueAt(a, sortKey), valueAt(b, sortKey));
      return sortDir === "asc" ? cmp : -cmp;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const totalRows = sorted.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const safePage = Math.min(page, totalPages);
  const paged = useMemo(
    () => sorted.slice((safePage - 1) * pageSize, safePage * pageSize),
    [sorted, safePage, pageSize]
  );

  const toggleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  return {
    search,
    setSearch,
    page: safePage,
    setPage,
    pageSize,
    setPageSize,
    sortKey,
    sortDir,
    toggleSort,
    filtered,
    sorted,
    paged,
    totalRows,
    totalPages
  };
}
